import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { LinearProgress } from '@mui/material';
import { Box } from '@mui/system';
import useTenantConfig from '../hooks/useTenantConfig';


const AuthContext = createContext({});

const AuthProvider = ({ children }) => {

    const [currentUser, setCurrentUser] = useState();
    const [deviceId, setDeviceId] = useState();
    const [loading, setLoading] = useState(true);

    const { apiUrl } = useTenantConfig();
        let X_Tenant = JSON.parse(localStorage.getItem('XymviewTenantConfig'))?.id || 'unidentified';


    //If there is a user already stored/logged in LocalStorage, lets use it
    useEffect(() => {
        setLoading(true);

        const loggedInUser = localStorage.getItem("XymviewUser");
        const storedDeviceId = localStorage.getItem("XymbotSensorId");


        if (loggedInUser) {
            console.log('Found already logged user, restoring session...');

            const foundUser = JSON.parse(loggedInUser);
            setCurrentUser(foundUser);
        }

        if (storedDeviceId) {
            console.log('Found already configured device id, restoring it...');
            setDeviceId(storedDeviceId);
        }



        setLoading(false);

    }, []);

    //Everytime user changes, we change our stored user
    useEffect(()=>{
        if (currentUser){
            localStorage.setItem('XymviewUser', JSON.stringify(currentUser));
        }
    }, [currentUser]);

    //Everytime deviceId changes, we change our stored deviceId
    useEffect(()=>{
        if (deviceId){
            localStorage.setItem('XymbotSensorId', deviceId);
        }
    }, [deviceId]);


    async function signup(email, password, role) {
        try{

            let response = await fetch(apiUrl + '/api/auth/register', {
                method: 'post',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({email: email, password: password, role: role}),
            })

            if (!response.ok) {
                const message = `An error has occured: ${response.status}`;
                throw new Error(message);
            }
            response = await response.json();

            if (response.error) throw new Error(response.message);

            return {error: false};
        } catch (error) {
            console.error('Error: ', error);
            return {error: true, message: error.message}
        }
    }





    async function login(email, password) {
        try {
           // setLoading(true);
            let response = await fetch(apiUrl + '/api/auth/login', {
                method: 'post',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'X-Tenant': X_Tenant,

                },
                body: JSON.stringify({email: email, password: password}),
            })

            if (!response.ok) {
                const message = `An error has occured: ${response.status}`;
                throw new Error(message);
            }
            response = await response.json();

            if (response.error) throw new Error(response.message);

            localStorage.setItem('XymviewUserApiBearerToken', response.token);
            setCurrentUser(response.user);
            setLoading(false);

            return {error: false, user: response.user};
        } catch (error) {
            console.error('Error: ', error);
            setLoading(false);
            return {error: true, message: error.message}
        }
    }


    const alternativeLogin = (user) => {
        setCurrentUser(user);
    }


    function logout() {
        setCurrentUser(undefined);
        localStorage.removeItem('XymviewUser');
    }

    async function resetPassword(email) {
        try {
            let response = await fetch(apiUrl + '/api/auth/request-password-reset', {
                method: 'post',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({email: email}),
            })

            if (!response.ok) {
                const message = `An error has occured: ${response.status}`;
                throw new Error(message);
            }
            response = await response.json();

            if (response.error) throw new Error(response.message);

            return {error: false};
        } catch (error) {
            console.error('Error: ', error);
            return {error: true, message: error.message}
        }
    }

    async function updateEmail(newEmail) {
        try {
            let response = await fetch(apiUrl + '/api/auth/updateEmail', {
                method: 'post',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({user_id: currentUser.id, new_email: newEmail}),
            })

            if (!response.ok) {
                const message = `An error has occured: ${response.status}`;
                throw new Error(message);
            }
            response = await response.json();

            if (response.error) throw new Error(response.message);

            let updatedUser = JSON.parse(JSON.stringify(currentUser));
            updatedUser.email = newEmail;

            setCurrentUser(updatedUser);

            return {error: false};
        } catch (error) {
            console.error('Error: ', error);
            return {error: true, message: error.message}
        }
    }

    async function updatePassword(newPassword) {
        try {
            let response = await fetch(apiUrl + '/api/auth/updatePassword', {
                method: 'post',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({user_id: currentUser.id, password: newPassword}),
            })

            if (!response.ok) {
                const message = `An error has occured: ${response.status}`;
                throw new Error(message);
            }
            response = await response.json();

            if (response.error) throw new Error(response.message);

            return {error: false};
        } catch (error) {
            console.error('Error: ', error);
            return {error: true, message: error.message}
        }
    }

    // useEffect(() => {
    //     const unsubscribe = auth.onAuthStateChanged((user) => {
    //         setCurrentUser(user);
    //         setLoading(false);
    //     });
    //
    //     return unsubscribe;
    // }, []);

    // Whenever the `value` passed into a provider changes,
    // the whole tree under the provider re-renders, and
    // that can be very costly! Even in this case, where
    // you only get re-renders when logging in and out
    // we want to keep things very performant.
    const memoedValue = useMemo(
        () => ({
            currentUser,
            setCurrentUser,
            deviceId,
            setDeviceId,
            login,
            alternativeLogin,
            signup,
            logout,
            resetPassword,
            updateEmail,
            updatePassword,
        }),
        [currentUser, deviceId, updateEmail, updatePassword]
    );

    return (
        <AuthContext.Provider value={memoedValue}>
            {!loading && children}
            {loading &&  <Box
                display="flex"
                width={"100%"}
                height={"100%"}
                alignItems="center"
                justifyContent="center"
                style={{backgroundColor: '#ffffff'}}
            >
                <LinearProgress variant={"indeterminate"} color={'primary'}  />
            </Box>}
        </AuthContext.Provider>
    );
}


export {AuthContext, AuthProvider};
